import { useState, useRef, useMemo, useCallback, useId } from 'react';
import type { ComboboxOption } from './Combobox.types';

interface UseComboboxStateProps {
  options: ComboboxOption[];
  selectedValues: ComboboxOption[];
  onSelectionChange: (selected: ComboboxOption[]) => void;
}

export function useComboboxState({ options, selectedValues, onSelectionChange }: UseComboboxStateProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const chevronClickedRef = useRef(false);

  const generatedId = useId();
  const comboboxId = `combobox-${generatedId}`;
  const listId = `${comboboxId}-listbox`;

  const displayOptions = useMemo(() => {
    if (!isOpen) return [];
    if (!searchTerm.trim()) return options;
    const term = searchTerm.trim().toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, searchTerm, isOpen]);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    setFocusedIndex(-1);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
  }, []);

  const toggleOption = useCallback(
    (option: ComboboxOption) => {
      const isSelected = selectedValues.some((v) => v.value === option.value);
      if (isSelected) {
        onSelectionChange(selectedValues.filter((v) => v.value !== option.value));
      } else {
        onSelectionChange([...selectedValues, option]);
      }
      setSearchTerm('');
      inputRef.current?.focus();
    },
    [selectedValues, onSelectionChange]
  );

  const removeValue = useCallback(
    (option: ComboboxOption) => {
      onSelectionChange(selectedValues.filter((v) => v.value !== option.value));
      inputRef.current?.focus();
    },
    [selectedValues, onSelectionChange]
  );

  const removeLastValue = useCallback(() => {
    if (selectedValues.length > 0) {
      onSelectionChange(selectedValues.slice(0, -1));
    }
  }, [selectedValues, onSelectionChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      if (!isOpen) {
        openDropdown();
      }
      setFocusedIndex(-1);
    },
    [isOpen, openDropdown]
  );

  const handleInputFocus = useCallback(() => {
    if (!chevronClickedRef.current) {
      openDropdown();
    }
    chevronClickedRef.current = false;
  }, [openDropdown]);

  const handleInputBlur = useCallback((e: React.FocusEvent) => {
    // If focus moved to another element inside the combobox (option click, chevron),
    // keep the dropdown open. relatedTarget is the element receiving focus.
    const nextFocused = e.relatedTarget as Node | null;
    if (
      nextFocused &&
      (containerRef.current?.contains(nextFocused) ||
        listRef.current?.contains(nextFocused))
    ) {
      return;
    }
    closeDropdown();
  }, [closeDropdown]);

  const handleChevronMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault(); // Prevent input blur
      chevronClickedRef.current = true;
      if (isOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
      inputRef.current?.focus();
    },
    [isOpen, openDropdown, closeDropdown]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't interfere with chevron button or tag dismiss clicks
      if (target.closest('[data-chevron-button]') || target.closest('button')) {
        return;
      }
      inputRef.current?.focus();
    },
    []
  );

  const isOptionSelected = useCallback(
    (option: ComboboxOption) => selectedValues.some((v) => v.value === option.value),
    [selectedValues]
  );

  return {
    searchTerm,
    setSearchTerm,
    isOpen,
    focusedIndex,
    setFocusedIndex,
    displayOptions,
    inputRef,
    containerRef,
    listRef,
    comboboxId,
    listId,
    openDropdown,
    closeDropdown,
    toggleOption,
    removeValue,
    removeLastValue,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleChevronMouseDown,
    handleContainerClick,
    isOptionSelected,
  };
}
